import React, {useEffect, useRef, useState, useCallback} from "react";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAP_API_KEY || "";

type Shape = {
    id: string;
    type: "circle" | "polygon" | "line";
    color: string;
} & (
    | {type: "circle"; center: [number, number]; radiusMeters: number}
    | {type: "polygon" | "line"; vertices: Array<[number, number]>}
);

type DrawingTool = "circle" | "polygon" | "line" | "none";

export function WebGLDrawPage() {
    const mapDivRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const mapRef = useRef<google.maps.Map | null>(null);
    const overlayRef = useRef<google.maps.OverlayView | null>(null);
    const glRef = useRef<WebGLRenderingContext | null>(null);

    const [googleLoaded, setGoogleLoaded] = useState(false);
    const [shapes, setShapes] = useState<Shape[]>([]);
    const [activeTool, setActiveTool] = useState<DrawingTool>("none");
    const [selectedColor, setSelectedColor] = useState("#3B82F6");
    const [currentDrawing, setCurrentDrawing] = useState<Array<[number, number]>>([]);
    const [selectedShapeId, setSelectedShapeId] = useState<string | null>(null);

    const shapesRef = useRef<Shape[]>([]);
    const currentDrawingRef = useRef<Array<[number, number]>>([]);
    const activeToolRef = useRef<DrawingTool>("none");
    const selectedColorRef = useRef<string>("#3B82F6");
    const mousePreviewRef = useRef<[number, number] | null>(null);

    useEffect(() => {
        shapesRef.current = shapes;
    }, [shapes]);

    useEffect(() => {
        currentDrawingRef.current = currentDrawing;
    }, [currentDrawing]);

    useEffect(() => {
        activeToolRef.current = activeTool;
    }, [activeTool]);

    useEffect(() => {
        selectedColorRef.current = selectedColor;
    }, [selectedColor]);

    // Load Google Maps with geometry library
    useEffect(() => {
        if (window.google?.maps) {
            setGoogleLoaded(true);
            return;
        }

        const script = document.createElement("script");
        script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&v=3.64&libraries=geometry`;
        script.async = true;
        script.onload = () => setGoogleLoaded(true);
        document.head.appendChild(script);
    }, []);

    // Initialize Google Map
    useEffect(() => {
        if (!googleLoaded || !mapDivRef.current || mapRef.current) return;

        const map = new google.maps.Map(mapDivRef.current, {
            center: {lat: 0, lng: 0},
            zoom: 2,
            mapTypeId: "roadmap",
            disableDefaultUI: false,
            zoomControl: true,
            mapTypeControl: true,
            streetViewControl: false,
            fullscreenControl: true
        });

        mapRef.current = map;

        // Create WebGL overlay
        class WebGLOverlay extends google.maps.OverlayView {
            canvas: HTMLCanvasElement | null = null;
            gl: WebGLRenderingContext | null = null;

            onAdd() {
                const canvas = document.createElement("canvas");
                canvas.style.position = "absolute";
                canvas.style.top = "0";
                canvas.style.left = "0";
                canvas.style.pointerEvents = "none";
                canvas.style.width = "100%";
                canvas.style.height = "100%";

                this.canvas = canvas;
                canvasRef.current = canvas;

                const panes = this.getPanes();
                panes?.overlayLayer.appendChild(canvas);

                const gl = canvas.getContext("webgl", {
                    alpha: true,
                    premultipliedAlpha: false,
                    antialias: true
                });

                if (!gl) {
                    console.error("WebGL not supported");
                    return;
                }

                this.gl = gl;
                glRef.current = gl;

                // Enable blending for transparency
                gl.enable(gl.BLEND);
                gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
            }

            draw() {
                const canvas = this.canvas;
                const gl = this.gl;
                if (!canvas || !gl) return;

                const projection = this.getProjection();
                if (!projection) return;

                const map = mapRef.current;
                if (!map) return;

                // Size canvas to fill the map div
                const mapDiv = map.getDiv();
                const width = mapDiv.offsetWidth;
                const height = mapDiv.offsetHeight;

                const dpr = window.devicePixelRatio || 1;
                canvas.width = width * dpr;
                canvas.height = height * dpr;

                gl.viewport(0, 0, canvas.width, canvas.height);
                gl.clearColor(0, 0, 0, 0);
                gl.clear(gl.COLOR_BUFFER_BIT);

                // Simple shader for filled shapes
                const vertexShaderSource = `
                    attribute vec2 a_position;
                    void main() {
                        gl_Position = vec4(a_position, 0.0, 1.0);
                        gl_PointSize = 1.0;
                    }
                `;

                const fragmentShaderSource = `
                    precision mediump float;
                    uniform vec4 u_color;
                    void main() {
                        gl_FragColor = u_color;
                    }
                `;

                // Create and compile shaders
                const vertexShader = gl.createShader(gl.VERTEX_SHADER)!;
                gl.shaderSource(vertexShader, vertexShaderSource);
                gl.compileShader(vertexShader);

                const fragmentShader = gl.createShader(gl.FRAGMENT_SHADER)!;
                gl.shaderSource(fragmentShader, fragmentShaderSource);
                gl.compileShader(fragmentShader);

                const program = gl.createProgram()!;
                gl.attachShader(program, vertexShader);
                gl.attachShader(program, fragmentShader);
                gl.linkProgram(program);
                gl.useProgram(program);

                const posLoc = gl.getAttribLocation(program, "a_position");
                const colorLoc = gl.getUniformLocation(program, "u_color");

                // Helper to convert lat/lng to NDC with world offset support
                const latLngToNDC = (lng: number, lat: number, worldOffset = 0): [number, number] | null => {
                    const adjustedLng = lng + (worldOffset * 360);
                    const latLng = new google.maps.LatLng(lat, adjustedLng);
                    const pixel = projection.fromLatLngToDivPixel(latLng);
                    if (!pixel) return null;

                    const x = (pixel.x / width) * 2 - 1;
                    const y = -((pixel.y / height) * 2 - 1);
                    return [x, y];
                };

                // Helper to draw a shape at multiple world offsets
                const drawShapeAtWorldOffsets = (drawFn: (offset: number) => void) => {
                    // Draw at offsets -1, 0, +1 to handle wrapping
                    for (let offset = -1; offset <= 1; offset++) {
                        drawFn(offset);
                    }
                };

                // Draw completed shapes at all world offsets
                for (const shape of shapesRef.current) {
                    const color = hexToRgba(shape.color, 0.5);
                    gl.uniform4f(colorLoc, color[0], color[1], color[2], color[3]);

                    drawShapeAtWorldOffsets((offset) => {
                        const makeLatLngToNDC = (lng: number, lat: number) => latLngToNDC(lng, lat, offset);

                        if (shape.type === "circle") {
                            drawCircle(gl, posLoc, shape as Shape & {type: "circle"}, makeLatLngToNDC, projection);
                        } else if (shape.type === "polygon") {
                            drawPolygon(gl, posLoc, shape as Shape & {type: "polygon"}, makeLatLngToNDC);
                        } else if (shape.type === "line") {
                            drawLine(gl, posLoc, shape as Shape & {type: "line"}, makeLatLngToNDC);
                        }
                    });
                }

                // Draw preview of current drawing at all world offsets
                const currentPoints = currentDrawingRef.current;
                const mousePos = mousePreviewRef.current;

                if (currentPoints.length > 0) {
                    const previewColor = hexToRgba(selectedColorRef.current, 0.3);
                    gl.uniform4f(colorLoc, previewColor[0], previewColor[1], previewColor[2], previewColor[3]);

                    drawShapeAtWorldOffsets((offset) => {
                        const makeLatLngToNDC = (lng: number, lat: number) => latLngToNDC(lng, lat, offset);

                        if (activeToolRef.current === "circle" && currentPoints.length === 1 && mousePos) {
                            // Preview circle from center to mouse
                            const center = currentPoints[0];
                            const radiusMeters = google.maps.geometry.spherical.computeDistanceBetween(
                                new google.maps.LatLng(center[1], center[0]),
                                new google.maps.LatLng(mousePos[1], mousePos[0])
                            );
                            drawCircle(gl, posLoc, {
                                type: "circle",
                                center,
                                radiusMeters
                            } as Shape & {type: "circle"}, makeLatLngToNDC, projection);
                        } else if (activeToolRef.current === "polygon" && currentPoints.length >= 1) {
                            // Preview polygon with mouse position as next point
                            const previewVertices = mousePos ? [...currentPoints, mousePos] : currentPoints;
                            drawPolygon(gl, posLoc, {type: "polygon", vertices: previewVertices} as Shape & {type: "polygon"}, makeLatLngToNDC);
                        } else if (activeToolRef.current === "line" && currentPoints.length >= 1) {
                            // Preview line with mouse position as next point
                            const previewVertices = mousePos ? [...currentPoints, mousePos] : currentPoints;
                            drawLine(gl, posLoc, {type: "line", vertices: previewVertices} as Shape & {type: "line"}, makeLatLngToNDC);
                        }
                    });
                }
            }

            onRemove() {
                if (this.canvas && this.canvas.parentElement) {
                    this.canvas.parentElement.removeChild(this.canvas);
                }
                this.canvas = null;
                this.gl = null;
            }
        }

        const overlay = new WebGLOverlay();
        overlay.setMap(map);
        overlayRef.current = overlay;

        // Redraw on map changes
        const redraw = () => overlay.draw();
        map.addListener("bounds_changed", redraw);
        map.addListener("zoom_changed", redraw);

    }, [googleLoaded]);

    // Define completeShape callback before it's used
    const completeShape = useCallback((shapeData: Shape) => {
        setShapes(prev => [...prev, shapeData]);
        setActiveTool("none");
        setCurrentDrawing([]);
    }, []);

    // Setup drawing listeners
    useEffect(() => {
        const map = mapRef.current;
        const overlay = overlayRef.current;
        if (!map || activeTool === "none") return;

        // Mousemove for preview
        const mouseMoveListener = map.addListener("mousemove", (e: google.maps.MapMouseEvent) => {
            if (!e.latLng) return;
            mousePreviewRef.current = [e.latLng.lng(), e.latLng.lat()];

            // Trigger redraw for preview
            if (overlay && currentDrawingRef.current.length > 0) {
                overlay.draw();
            }
        });

        const clickListener = map.addListener("click", (e: google.maps.MapMouseEvent) => {
            if (!e.latLng) return;
            const lat = e.latLng.lat();
            const lng = e.latLng.lng();
            const point: [number, number] = [lng, lat];

            if (activeTool === "circle") {
                const current = currentDrawingRef.current;
                if (current.length === 0) {
                    // First click - set center
                    setCurrentDrawing([point]);
                } else {
                    // Second click - complete circle
                    const center = current[0];
                    const radiusMeters = google.maps.geometry.spherical.computeDistanceBetween(
                        new google.maps.LatLng(center[1], center[0]),
                        new google.maps.LatLng(lat, lng)
                    );

                    completeShape({
                        id: Math.random().toString(36).substr(2, 9),
                        type: "circle",
                        center,
                        radiusMeters,
                        color: selectedColor
                    });
                }
            } else {
                // Polygon or line - add point
                setCurrentDrawing(prev => [...prev, point]);
            }
        });

        // Handle Enter key to finish polygon/line
        const keyListener = (e: KeyboardEvent) => {
            if (e.key === "Enter" && (activeTool === "polygon" || activeTool === "line")) {
                const current = currentDrawingRef.current;
                if (current.length >= 2) {
                    completeShape({
                        id: Math.random().toString(36).substr(2, 9),
                        type: activeTool,
                        vertices: current,
                        color: selectedColor
                    });
                }
            } else if (e.key === "Escape") {
                setActiveTool("none");
                setCurrentDrawing([]);
            }
        };

        document.addEventListener("keydown", keyListener);

        return () => {
            google.maps.event.removeListener(mouseMoveListener);
            google.maps.event.removeListener(clickListener);
            document.removeEventListener("keydown", keyListener);
            mousePreviewRef.current = null;
        };
    }, [activeTool, completeShape, selectedColor]);

    // Redraw overlay when shapes change
    useEffect(() => {
        const overlay = overlayRef.current;
        if (overlay) {
            overlay.draw();
        }
    }, [shapes, currentDrawing]);

    // Helper functions
    const hexToRgba = (hex: string, alpha: number): [number, number, number, number] => {
        const r = parseInt(hex.slice(1, 3), 16) / 255;
        const g = parseInt(hex.slice(3, 5), 16) / 255;
        const b = parseInt(hex.slice(5, 7), 16) / 255;
        return [r, g, b, alpha];
    };

    const drawCircle = (
        gl: WebGLRenderingContext,
        posLoc: number,
        shape: Shape & {type: "circle"},
        latLngToNDC: (lng: number, lat: number) => [number, number] | null,
        projection: google.maps.MapCanvasProjection
    ) => {
        // Use more segments for accurate rendering near poles
        const segments = 128;
        const perimeterPoints: Array<[number, number]> = [];
        const center = new google.maps.LatLng(shape.center[1], shape.center[0]);

        // Generate perimeter points
        for (let i = 0; i <= segments; i++) {
            const bearing = (i / segments) * 360;
            const point = google.maps.geometry.spherical.computeOffset(center, shape.radiusMeters, bearing);
            const ndc = latLngToNDC(point.lng(), point.lat());
            if (ndc) {
                perimeterPoints.push(ndc);
            }
        }

        if (perimeterPoints.length < 3) return;

        // Draw filled circle using triangle fan from computed centroid in screen space
        const positions: number[] = [];

        // Calculate screen-space centroid of the perimeter
        let centerX = 0, centerY = 0;
        for (const [x, y] of perimeterPoints) {
            centerX += x;
            centerY += y;
        }
        centerX /= perimeterPoints.length;
        centerY /= perimeterPoints.length;

        // Triangle fan from screen-space center
        positions.push(centerX, centerY);
        for (const [x, y] of perimeterPoints) {
            positions.push(x, y);
        }

        const buffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(positions), gl.STATIC_DRAW);
        gl.enableVertexAttribArray(posLoc);
        gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);
        gl.drawArrays(gl.TRIANGLE_FAN, 0, positions.length / 2);
    };

    const drawPolygon = (
        gl: WebGLRenderingContext,
        posLoc: number,
        shape: Shape & {type: "polygon"},
        latLngToNDC: (lng: number, lat: number) => [number, number] | null
    ) => {
        const positions: number[] = [];

        for (const [lng, lat] of shape.vertices) {
            const ndc = latLngToNDC(lng, lat);
            if (ndc) {
                positions.push(ndc[0], ndc[1]);
            }
        }

        if (positions.length < 6) return;

        const buffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(positions), gl.STATIC_DRAW);
        gl.enableVertexAttribArray(posLoc);
        gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);
        gl.drawArrays(gl.TRIANGLE_FAN, 0, positions.length / 2);
    };

    const drawLine = (
        gl: WebGLRenderingContext,
        posLoc: number,
        shape: Shape & {type: "line"},
        latLngToNDC: (lng: number, lat: number) => [number, number] | null
    ) => {
        const positions: number[] = [];

        for (const [lng, lat] of shape.vertices) {
            const ndc = latLngToNDC(lng, lat);
            if (ndc) {
                positions.push(ndc[0], ndc[1]);
            }
        }

        if (positions.length < 4) return;

        const buffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(positions), gl.STATIC_DRAW);
        gl.enableVertexAttribArray(posLoc);
        gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);
        gl.drawArrays(gl.LINE_STRIP, 0, positions.length / 2);
    };

    const startDrawing = (tool: DrawingTool) => {
        setActiveTool(tool);
        setCurrentDrawing([]);
        setSelectedShapeId(null);
    };

    const deleteShape = (id: string) => {
        setShapes(prev => prev.filter(s => s.id !== id));
        setSelectedShapeId(null);
    };

    const clearAll = () => {
        setShapes([]);
        setSelectedShapeId(null);
        setCurrentDrawing([]);
        setActiveTool("none");
    };

    return (
        <div className="h-screen flex flex-col pt-36">
            <div className="bg-white border-b border-gray-200 px-6 py-4">
                <h1 className="text-2xl font-semibold text-gray-800">WebGL Drawing Tool</h1>
                <p className="text-sm text-gray-600 mt-1">Draw geographic shapes with accurate calculations</p>
            </div>

            <div className="flex-1 flex overflow-hidden">
                <div className="w-80 bg-white border-r border-gray-200 flex flex-col">
                    <div className="p-4 border-b border-gray-100">
                        <h2 className="font-semibold text-gray-800 mb-3">Drawing Tools</h2>
                        <div className="space-y-2">
                            <button
                                onClick={() => startDrawing("circle")}
                                className={`w-full px-4 py-2.5 rounded-lg font-medium transition-all ${
                                    activeTool === "circle"
                                        ? "bg-blue-600 text-white shadow-md"
                                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                }`}
                            >
                                Circle
                            </button>
                            <button
                                onClick={() => startDrawing("polygon")}
                                className={`w-full px-4 py-2.5 rounded-lg font-medium transition-all ${
                                    activeTool === "polygon"
                                        ? "bg-blue-600 text-white shadow-md"
                                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                }`}
                            >
                                Polygon
                            </button>
                            <button
                                onClick={() => startDrawing("line")}
                                className={`w-full px-4 py-2.5 rounded-lg font-medium transition-all ${
                                    activeTool === "line"
                                        ? "bg-blue-600 text-white shadow-md"
                                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                }`}
                            >
                                Line
                            </button>
                        </div>

                        {activeTool !== "none" && (
                            <div className="mt-4 p-3 bg-blue-50 rounded-lg text-sm text-gray-700">
                                {activeTool === "circle" && "Click center, then click to set radius"}
                                {activeTool === "polygon" && "Click points to draw polygon, press Enter to finish"}
                                {activeTool === "line" && "Click points to draw line, press Enter to finish"}
                                <div className="mt-1 text-xs text-gray-600">Press Escape to cancel</div>
                            </div>
                        )}

                        <div className="mt-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Shape Color
                            </label>
                            <input
                                type="color"
                                value={selectedColor}
                                onChange={(e) => setSelectedColor(e.target.value)}
                                className="w-full h-10 rounded cursor-pointer"
                            />
                        </div>

                        <button
                            onClick={clearAll}
                            className="w-full mt-4 px-4 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors"
                        >
                            Clear All
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto p-4">
                        <h2 className="font-semibold text-gray-800 mb-3">Shapes ({shapes.length})</h2>
                        <div className="space-y-2">
                            {shapes.map((shape) => (
                                <div
                                    key={shape.id}
                                    onClick={() => setSelectedShapeId(shape.id)}
                                    className={`p-3 rounded-lg cursor-pointer transition-all ${
                                        selectedShapeId === shape.id
                                            ? "bg-blue-50 border-2 border-blue-600"
                                            : "bg-gray-50 border-2 border-transparent hover:bg-gray-100"
                                    }`}
                                >
                                    <div className="flex justify-between items-start">
                                        <div className="flex-1">
                                            <div className="font-medium text-gray-800 capitalize flex items-center gap-2">
                                                <div
                                                    className="w-4 h-4 rounded"
                                                    style={{backgroundColor: shape.color}}
                                                />
                                                {shape.type}
                                            </div>
                                            <div className="text-xs text-gray-600 mt-1">
                                                {shape.type === "circle" && `Radius: ${(shape.radiusMeters / 1000).toFixed(1)}km`}
                                                {(shape.type === "polygon" || shape.type === "line") && `${shape.vertices.length} points`}
                                            </div>
                                        </div>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                deleteShape(shape.id);
                                            }}
                                            className="text-red-600 hover:text-red-800 font-medium text-sm"
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="flex-1 relative">
                    <div ref={mapDivRef} className="w-full h-full" />
                </div>
            </div>
        </div>
    );
}
